import styled, { css } from 'styled-components'

import { Array } from '@blade/std-lib'
import { Numeric, Typography } from '@blade/ui-kit'
import { CurrencyCell } from '@root/entities/currency/ui'
import { TransactionAgeCell } from '@root/entities/network/ui'
import { SwapDirectionCell } from '@root/entities/trade-type/ui'
import { useAppRoutes } from '@root/features/routing'
import { FilterByMaker } from '@root/features/trade/filters'
import { StringUtil } from '@root/shared/core'
import { MarkerCapShortFormatted } from '@root/shared/core/format'

import { TradeAmountCell } from './trade-amount-cell'
import { TradeAmountChartCell } from './trade-amount-chart-cell'
import { TradeMakerCell } from './trade-maker-cell'
import { TradeTotalCell } from './trade-total-cell'
import { TradeTotalHeader } from './trade-total-header'

import type { CurrencyModel } from '@root/entities/currency'
import type { PairAddress, PairModel } from '@root/entities/pair'
import type { Trade, TradeType } from '@root/entities/trade'
import type { TradeTableColumnId } from '@root/features/trade/tables/trades-table/atoms/reatom-trades-table'
import type { ChildrenProps } from '@root/shared/react'
import type { Column } from '@root/shared/ui-kit'

const CurrencyCellWrapper = ({
  currency,
  pairAddress,
}: {
  currency: CurrencyModel
  pairAddress: PairAddress
}) => {
  const { navigateToTrading } = useAppRoutes()

  return (
    <CurrencyCell
      pairAddress={pairAddress}
      currency={currency}
      tierRank={undefined}
      onClick={() => {
        navigateToTrading(pairAddress, currency.network)
      }}
    />
  )
}

export const createTradeTableColumns = ({
  withMakerFilter = true,
  withCurrency = false,
  aliasUserWallet,
  showOwner,
  pair,
}: {
  withMakerFilter?: boolean
  withCurrency?: boolean
  aliasUserWallet?: boolean
  showOwner?: boolean
  pair: PairModel | undefined
}): Array<Column<Trade.Trade, TradeTableColumnId>> => {
  const columns: Array<Column<Trade.Trade, TradeTableColumnId> | undefined> = [
    {
      id: 'timestamp' as const,
      header: ({ t }) => t('common.age'),
      render: ({ value }) => (
        <TransactionAgeCell
          network={value.network}
          timestamp={value.timestamp}
          transactionHash={value.transactionHash}
        />
      ),
      minWidth: 80,
      maxWidth: 120,
      sortable: true,
    },
    {
      id: 'type' as const,
      header: ({ t }) => t('common.type'),
      render: ({ value }) => <SwapDirectionCell type={value.type} />,
      width: 40,
    },
    withCurrency
      ? {
          id: 'quote-currency' as const,
          header: ({ t }) => t('token'),
          render: ({ value }) =>
            value.quoteCurrency ? (
              <CurrencyCellWrapper
                currency={value.quoteCurrency}
                pairAddress={value.pairAddress}
              />
            ) : undefined,
          minWidth: 100,
        }
      : undefined,
    {
      id: 'price' as const,
      header: ({ t }) => t('common.price'),
      render: ({ value }) => (
        <CheckTradeType type={value.type}>
          <TypeTypography>
            <Numeric
              value={value.currencyPriceUsd}
              preset="currency-amount-short"
              currency="$"
            />
          </TypeTypography>
        </CheckTradeType>
      ),
      minWidth: 60,
    },
    {
      id: 'mcap' as const,
      header: ({ t }) => t('common.mkt_cap'),
      render: ({ value }) => (
        <CheckTradeType type={value.type}>
          <TypeTypography>
            <MarkerCapShortFormatted mcap={value.marketCapUsd} />
          </TypeTypography>
        </CheckTradeType>
      ),
      justify: 'center',
      minWidth: 50,
    },
    {
      id: 'amount' as const,
      header: ({ t }) => t('common.amount'),
      render: ({ value }) => <TradeAmountCell value={value} />,
      justify: 'end',
      minWidth: 50,
    },
    {
      id: 'total-usd' as const,
      header: (props) => (
        <TradeTotalHeader {...props} pair={pair} currencyType="usd" />
      ),
      render: ({ value }) => (
        <CheckTradeType type={value.type}>
          <TradeAmountChartCell value={value} />
          <TradeTotalCell pair={pair} value={value} fixedCurrency="usd" />
        </CheckTradeType>
      ),
      getValue: (value) => value.totalUsd,
      minWidth: 120,
      filterable: true,
      justify: 'end',
      css: css`
        position: relative;
        height: 40px;
      `,
    },
    {
      id: 'total-base' as const,
      header: (props) => (
        <TradeTotalHeader {...props} pair={pair} currencyType="base" />
      ),
      render: ({ value }) => (
        <CheckTradeType type={value.type}>
          <TradeTotalCell
            value={value}
            fixedCurrency={withCurrency ? 'native' : 'base'}
            pair={pair}
          />
        </CheckTradeType>
      ),
      getValue: (value) => value.totalUsd,
      minWidth: 80,
      justify: 'end',
      filterable: true,
    },
    {
      id: 'maker',
      header: ({ filter, setColumnFilter, clearColumnFilter, columnId, t }) =>
        withMakerFilter ? (
          <FilterByMaker
            filter={filter}
            setColumnFilter={setColumnFilter}
            clearColumnFilter={clearColumnFilter}
            columnId={columnId}
          />
        ) : (
          StringUtil.capitalize(t('wallet_one'))
        ),
      render: (props) => (
        <TradeMakerCell
          {...props}
          pair={pair}
          aliasUserWallet={aliasUserWallet}
          showOwner={showOwner}
        />
      ),
      getValue: (value) => value.maker.address,
      minWidth: 220,
      justify: 'end',
      filterable: true,
    },
  ]
  return Array.compact(columns)
}

const TypeTypography = styled(Typography)`
  color: var(--row-color);
`

const CheckTradeType = ({
  type,
  children,
}: { type: TradeType.TradeType } & ChildrenProps) => {
  if (type === 'lpBurn' || type === 'quoteBurn') {
    return <Hyphen>-</Hyphen>
  }
  return children
}

const Hyphen = styled.span`
  color: var(--text-9);
  align-self: center;
`
